import { Injectable, signal } from '@angular/core';

/** Parâmetros do benchmark simulado escolhido no gráfico de Performance da Dívida. */
export interface EconomyBenchmarkSimulation {
  indexer: 'CDI' | 'PRE';
  spread: number;
}

/**
 * Período do gráfico aplicado aos cards de economia (card 3323): os cards passam a mostrar a economia
 * na data final (`to`) e, com início (`from`), quanto foi gerado dentro do período. Datas em
 * YYYY-MM-DD.
 */
export interface EconomyPeriodSelection {
  from: string | null;
  to: string;
}

/**
 * Estado compartilhado da simulação de benchmark do Cenário de Economia.
 *
 * O `DebtPerformanceChartComponent` (dono do seletor de benchmark) publica a simulação ao
 * clicar "Aplicar" e limpa ao voltar ao padrão (troca de empresa/recarga); o
 * `EconomyScenarioCardsComponent` consome o sinal para recalcular os cards contra o benchmark
 * simulado e pode pedir o retorno ao benchmark oficial (`requestReset`), que o gráfico observa
 * para recarregar a curva padrão. Comunicação entre irmãos sem passar pelo componente-pai
 * (que está acima do teto de linhas e não deve crescer).
 */
@Injectable({ providedIn: 'root' })
export class EconomyScenarioSimulationService {
  private readonly _simulation = signal<EconomyBenchmarkSimulation | null>(null);
  private readonly _resetRequests = signal(0);
  private readonly _period = signal<EconomyPeriodSelection | null>(null);
  private readonly _periodResetRequests = signal(0);

  /** Simulação ativa (null = benchmark oficial da empresa). */
  readonly simulation = this._simulation.asReadonly();

  /** Contador incremental de pedidos de "voltar ao benchmark oficial" (consumido pelo gráfico). */
  readonly resetRequests = this._resetRequests.asReadonly();

  /** Período aplicado aos cards (null = cards em hoje). */
  readonly period = this._period.asReadonly();

  /** Contador de pedidos de "voltar para hoje" vindos dos cards (o gráfico volta o período para Tudo). */
  readonly periodResetRequests = this._periodResetRequests.asReadonly();

  /** Publica o período do gráfico nos cards. Independente da simulação de benchmark. */
  applyPeriod(period: EconomyPeriodSelection): void {
    this._period.set({ ...period });
  }

  /** Tira o período dos cards (troca de empresa, recarga do gráfico) sem mexer no gráfico. */
  clearPeriod(): void {
    this._period.set(null);
  }

  /** Cards voltam para hoje e o gráfico é avisado. */
  requestPeriodReset(): void {
    this._period.set(null);
    this._periodResetRequests.update(n => n + 1);
  }

  /** Publica a simulação aplicada no gráfico. */
  apply(simulation: EconomyBenchmarkSimulation): void {
    this._simulation.set(simulation);
  }

  /** Limpa a simulação (ex.: troca de empresa ou recarga do gráfico) sem pedir reset da curva. */
  clear(): void {
    this._simulation.set(null);
  }

  /** Pede o retorno ao benchmark oficial: limpa a simulação e notifica o gráfico para recarregar. */
  requestReset(): void {
    this._simulation.set(null);
    this._resetRequests.update(n => n + 1);
  }
}
